import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { AddOnDiscountProduct } from "../../types/cart";
import { getProductUrl } from "../../core/productUrl.core";
import { formatCurrency } from "../../core/currency.core";
import { useTranslation } from "../../core/translation.core";
import { addAddOnProduct } from "../../core/cart2.core";
import { handleAddOnError } from "../../core/addOnDiscount.core";
import { invalidateCartQueries } from "../../hooks/cart/invalidateCart";

interface AddOnProductCardProps {
  product: AddOnDiscountProduct;
}

export function AddOnProductCard({ product }: AddOnProductCardProps) {
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();
  const [quantity, setQuantity] = useState(1);
  const [isAdding, setIsAdding] = useState(false);

  const addOnProduct = product.product;
  const isEligible = product.eligible !== false;
  const hasReachedMaxQuantity = product.maxQuantity !== undefined && quantity >= product.maxQuantity;

  const handleAddToCart = async () => {
    setIsAdding(true);
    try {
      const submitQuantity = Math.min(quantity, product.maxQuantity ?? quantity);

      await addAddOnProduct({
        productId: product.product.id,
        addOnDiscountId: product.addOnDiscountId,
        addOnProductId: product.attachToProductId,
        quantity: submitQuantity,
      });

      toast.success(t(msg.product.addToCartSuccessfully));
      invalidateCartQueries(queryClient);
    } catch (error: any) {
      const errorMessage = handleAddOnError(error, t, msg);
      toast.error(errorMessage);
    } finally {
      setIsAdding(false);
    }
  };

  const discountPercentage = parseFloat(String(product.discountPercentage));

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow relative">
      {discountPercentage > 0 && (
        <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded z-10">
          {t(msg.addOnDiscount.save).replace("{percentage}", String(product.discountPercentage))}
        </div>
      )}

      <div className="relative w-full h-40 mb-3">
        <Link href={getProductUrl(addOnProduct)} target="_blank" className="flex justify-center">
          {addOnProduct.featureMedia ? (
            <Image
              src={addOnProduct.featureMedia.url}
              width={160}
              height={160}
              alt={addOnProduct.name}
              className="object-contain"
              unoptimized
            />
          ) : (
            <Image src="/images/logo-square.webp" fill alt={addOnProduct.name} className="object-contain" />
          )}
        </Link>
      </div>

      <h3 className="text-sm font-medium mb-2 line-clamp-2 min-h-[40px]">
        <Link href={getProductUrl(addOnProduct)} className="hover:text-guru-liver">
          {addOnProduct.name}
        </Link>
      </h3>

      <div className="mb-3">
        <div className="flex items-center gap-2">
          <span className="text-gray-400 line-through text-sm">{formatCurrency(addOnProduct.price, addOnProduct.currency)}</span>
          <span className="text-red-600 font-bold text-lg">{formatCurrency(product.discountPrice, addOnProduct.currency)}</span>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <button
          onClick={() => setQuantity(Math.max(1, quantity - 1))}
          className="w-8 h-8 border border-gray-300 rounded flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={quantity <= 1}
        >
          -
        </button>
        <span className="w-12 text-center font-medium">{quantity}</span>
        <button
          onClick={() => setQuantity(quantity + 1)}
          className="w-8 h-8 border border-gray-300 rounded flex items-center justify-center hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={hasReachedMaxQuantity}
        >
          +
        </button>
      </div>

      {hasReachedMaxQuantity && (
        <p className="text-xs  mb-2">{t(msg.addOnDiscount.maxQuantityInfo).replace("{quantity}", String(product.maxQuantity))}</p>
      )}

      {!isEligible && <p className="text-xs text-amber-600 mb-2">{t(msg.addOnDiscount.tierThresholdNotMet)}</p>}

      <button
        onClick={handleAddToCart}
        disabled={isAdding || quantity < 1 || !isEligible}
        className="w-full bg-guru-liver text-white py-2 rounded hover:bg-opacity-90 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {isAdding ? t(msg.general.loading) : t(msg.addOnDiscount.addToCart)}
      </button>
    </div>
  );
}
